// Member court reservation hour math — v2 (3 buckets + policy evaluation).
//
// Buckets: a booking is classified WEEKEND (entire booking, no prime split)
// when its date falls on Sat/Sun; otherwise it splits into PRIME vs REGULAR
// (non-prime) by the club's rentalTimeBlocks flagged isPrime: true.
//
// Phase 1 (observation): evaluatePolicies() returns would-be violations as
// flags; nothing here enforces. Enforcement later flips on flag presence.
//
// Member hours are per-booking-hour × courts when multi-court (each court
// consumes hours); single-court bookings behave as before.

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "HH:MM" -> minutes since midnight */
function toMins(t) {
  const [h, m] = String(t).split(':').map(Number);
  return h * 60 + (m || 0);
}

/** True when the local date string falls on Saturday or Sunday. */
export function isWeekend(date) {
  const day = new Date(`${date}T12:00:00`).getDay();
  return day === 0 || day === 6;
}

/**
 * v1 two-bucket split — retained for backward compatibility.
 */
export function splitHours(startTime, endTime, date, primeBlocks) {
  const startMins = toMins(startTime);
  const endMins = toMins(endTime);
  if (!(endMins > startMins)) return { regularHours: 0, primeHours: 0 };

  const dayAbbr = DAY_ABBR[new Date(`${date}T12:00:00`).getDay()];

  const applicable = (primeBlocks || []).filter(pb =>
    Array.isArray(pb.days) && pb.days.includes(dayAbbr) &&
    toMins(pb.startTime) < endMins &&
    toMins(pb.endTime) > startMins
  );

  if (applicable.length === 0) {
    return { regularHours: (endMins - startMins) / 60, primeHours: 0 };
  }

  const intervals = applicable
    .map(pb => [Math.max(startMins, toMins(pb.startTime)), Math.min(endMins, toMins(pb.endTime))])
    .filter(([s, e]) => e > s)
    .sort((a, b) => a[0] - b[0]);

  let primeMins = 0;
  let curStart = null;
  let curEnd = null;
  for (const [s, e] of intervals) {
    if (curStart === null) { curStart = s; curEnd = e; continue; }
    if (s <= curEnd) { curEnd = Math.max(curEnd, e); }
    else { primeMins += curEnd - curStart; curStart = s; curEnd = e; }
  }
  if (curStart !== null) primeMins += curEnd - curStart;

  const totalMins = endMins - startMins;
  return {
    regularHours: (totalMins - primeMins) / 60,
    primeHours: primeMins / 60,
  };
}

/**
 * v2 three-bucket split. Weekend wins: on Sat/Sun the entire booking is
 * weekendHours with no prime/regular distinction. Weekdays split by the
 * prime blocks exactly as v1.
 *
 * @param {number} courtCount  hours multiply by courts (default 1)
 * @returns {{ regularHours, primeHours, weekendHours }}
 */
export function splitHours3(startTime, endTime, date, primeBlocks, courtCount = 1) {
  const n = Math.max(1, Number(courtCount) || 1);
  if (isWeekend(date)) {
    const mins = Math.max(0, toMins(endTime) - toMins(startTime));
    return { regularHours: 0, primeHours: 0, weekendHours: (mins / 60) * n };
  }
  const two = splitHours(startTime, endTime, date, primeBlocks);
  return {
    regularHours: two.regularHours * n,
    primeHours: two.primeHours * n,
    weekendHours: 0,
  };
}

/**
 * v1 two-bucket overage — retained for backward compatibility.
 */
export function calcOverage(bookingSplit, usage, tier) {
  const regularAlloc = Number(tier?.weeklyRegularHours) || 0;
  const primeAlloc = Number(tier?.weeklyPrimeHours) || 0;
  const regularRate = Number(tier?.overageRegularRate) || 0;
  const primeRate = Number(tier?.overagePrimeRate) || 0;

  const regularUsed = Number(usage?.regularUsed) || 0;
  const primeUsed = Number(usage?.primeUsed) || 0;

  const regularRemaining = Math.max(0, regularAlloc - regularUsed);
  const primeRemaining = Math.max(0, primeAlloc - primeUsed);

  const overageRegularHours = Math.max(0, (bookingSplit.regularHours || 0) - regularRemaining);
  const overagePrimeHours = Math.max(0, (bookingSplit.primeHours || 0) - primeRemaining);

  const overageAmount =
    Math.round((overageRegularHours * regularRate + overagePrimeHours * primeRate) * 100) / 100;

  return {
    overageRegularHours,
    overagePrimeHours,
    overageAmount,
    remainingRegularAfter: Math.max(0, regularRemaining - (bookingSplit.regularHours || 0)),
    remainingPrimeAfter: Math.max(0, primeRemaining - (bookingSplit.primeHours || 0)),
  };
}

/**
 * v2 three-bucket overage.
 *
 * Tier fields (all optional; missing = 0 alloc / $0 rate):
 *   weeklyRegularHours, weeklyPrimeHours, weeklyWeekendHours
 *   overageRegularRate, overagePrimeRate, overageWeekendRate
 * Usage fields: regularUsed, primeUsed, weekendUsed (missing = 0).
 */
export function calcOverage3(bookingSplit, usage, tier) {
  const alloc = {
    regular: Number(tier?.weeklyRegularHours) || 0,
    prime: Number(tier?.weeklyPrimeHours) || 0,
    weekend: Number(tier?.weeklyWeekendHours) || 0,
  };
  const rate = {
    regular: Number(tier?.overageRegularRate) || 0,
    prime: Number(tier?.overagePrimeRate) || 0,
    weekend: Number(tier?.overageWeekendRate) || 0,
  };
  const used = {
    regular: Number(usage?.regularUsed) || 0,
    prime: Number(usage?.primeUsed) || 0,
    weekend: Number(usage?.weekendUsed) || 0,
  };
  const want = {
    regular: bookingSplit.regularHours || 0,
    prime: bookingSplit.primeHours || 0,
    weekend: bookingSplit.weekendHours || 0,
  };

  const out = { overageAmount: 0 };
  for (const b of ['regular', 'prime', 'weekend']) {
    // A bucket participates in overage only when configured: alloc > 0 or a
    // rate is set. Fully unconfigured buckets (0/0) are uncapped — otherwise
    // an empty tier would flag every booked hour as overage.
    const configured = alloc[b] > 0 || rate[b] > 0;
    const remaining = Math.max(0, alloc[b] - used[b]);
    const over = configured ? Math.max(0, want[b] - remaining) : 0;
    const cap = b.charAt(0).toUpperCase() + b.slice(1);
    out[`overage${cap}Hours`] = over;
    out[`remaining${cap}After`] = Math.max(0, remaining - want[b]);
    out.overageAmount += over * rate[b];
  }
  out.overageAmount = Math.round(out.overageAmount * 100) / 100;
  return out;
}

/**
 * Sum splits of a member's other bookings on one date into daily usage.
 * Bookings must be non-cancelled and all share `date`.
 */
export function sumDaySplits(dayBookings, primeBlocks) {
  const total = { regularHours: 0, primeHours: 0, weekendHours: 0, courts: new Set(), bookings: 0 };
  for (const b of dayBookings || []) {
    const courts = Array.isArray(b.courts) ? b.courts : [];
    const s = splitHours3(b.startTime, b.endTime, b.date, primeBlocks, courts.length || 1);
    total.regularHours += s.regularHours;
    total.primeHours += s.primeHours;
    total.weekendHours += s.weekendHours;
    courts.forEach(c => total.courts.add(c));
    total.bookings += 1;
  }
  return total;
}

/**
 * Evaluate tier policies for a proposed booking. Pure; returns flags only —
 * Phase 1 records them, Phase 2 will enforce on them.
 *
 * @param {object} p
 *   p.split        splitHours3() of the proposed booking
 *   p.usage        memberUsage doc for the week ({} ok)
 *   p.tier         membershipTiers doc
 *   p.date         booking date "YYYY-MM-DD"
 *   p.todayStr     today "YYYY-MM-DD" (club-local)
 *   p.dayUsage     sumDaySplits() of the member's OTHER bookings that date
 *   p.bookingCourts array of court ids on the proposed booking
 * @returns {{ flags: string[], overage: object }}
 */
export function evaluatePolicies(p) {
  const { split, usage, tier, date, todayStr, dayUsage, bookingCourts } = p;
  const flags = [];
  const t = tier || {};

  // Prime blocked entirely for this tier (e.g. Silver)
  if (t.primeBlocked && (split.primeHours || 0) > 0) flags.push('prime_blocked_tier');

  // Booking window
  const maxDays = Number(t.maxAdvanceDays) || 0;
  if (maxDays > 0 && date && todayStr) {
    const diffDays = Math.round((new Date(`${date}T12:00:00`) - new Date(`${todayStr}T12:00:00`)) / 86400000);
    if (diffDays > maxDays) flags.push('advance_window_exceeded');
  }

  // Daily caps (blank/0 cap = uncapped)
  const day = dayUsage || { regularHours: 0, primeHours: 0, weekendHours: 0, courts: new Set(), bookings: 0 };
  const dailyCaps = {
    regular: Number(t.dailyRegularHours) || 0,
    prime: Number(t.dailyPrimeHours) || 0,
    weekend: Number(t.dailyWeekendHours) || 0,
  };
  const dayKey = { regular: 'regularHours', prime: 'primeHours', weekend: 'weekendHours' };
  for (const b of ['regular', 'prime', 'weekend']) {
    if (dailyCaps[b] > 0 && (day[dayKey[b]] || 0) + (split[dayKey[b]] || 0) > dailyCaps[b]) {
      flags.push(`daily_${b}_exceeded`);
    }
  }

  // Court limit: distinct courts booked that day, including this booking's
  const limit = Number(t.maxCourtsPerDay ?? t.courtLimit) || 0;
  if (limit > 0) {
    const courts = new Set(day.courts instanceof Set ? day.courts : (day.courts || []));
    (bookingCourts || []).forEach(c => courts.add(c));
    if (courts.size > limit) flags.push('court_limit_exceeded');
  }

  // Weekly overage (also flags when caps exceeded — overage IS the weekly signal)
  const overage = calcOverage3(split, usage, t);
  if (overage.overageRegularHours > 0) flags.push('weekly_regular_exceeded');
  if (overage.overagePrimeHours > 0) flags.push('weekly_prime_exceeded');
  if (overage.overageWeekendHours > 0) flags.push('weekly_weekend_exceeded');

  return { flags, overage };
}
